export const createStyles = (theme: any) => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    width: '100%',
    maxWidth: { xs: '100%', md: 800 },
    mx: { md: 'auto' },
    minWidth: 0,
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: { xs: 1.5, sm: 2 },
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: { xs: 1, sm: 1.5 },
    minWidth: 0,
    px: 2,
    pt: 2,
    pb: 1.5,
  },
  author: {
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    minWidth: 0,
    gap: { xs: 1, sm: 1.5 },
  },
  avatar: {
    flexShrink: 0,
    width: 40,
    height: 40,
    cursor: 'default',
  },
  authorDetails: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minWidth: 0,
    gap: 0.25,
  },
  authorNameRow: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    gap: 0.75,
  },
  authorName: {
    overflow: 'hidden',
    minWidth: 0,
    color: theme.palette.text.primary,
    fontFamily: theme.typography.fontFamily,
    fontSize: '1rem',
    fontWeight: theme.typography.fontWeightMedium ?? 500,
    fontStyle: 'normal',
    lineHeight: 1.5,
    letterSpacing: '0.15px',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  roleBadge: {
    backgroundColor: theme.palette.mode === 'dark' ? '#B4E8FB' : '#027D99',
    color:
      theme.palette.mode === 'dark'
        ? '#005B6C'
        : theme.palette.primary?.contrastText ?? '#fff',
    cursor: 'default',
  },
  timestamp: {
    overflow: 'hidden',
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.text.secondary,
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: '12px',
    lineHeight: 1.66,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  options: {
    flexShrink: 0,
    mt: -0.5,
    mr: -1,
    color: theme.palette.text.secondary,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    px: 2,
    pb: '12px',
    color: theme.palette.text.primary,
    fontFamily: theme.typography.fontFamily,
    fontSize: { xs: '0.9375rem', sm: '1rem' },
    lineHeight: { xs: 1.5, sm: 1.55 },
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
    // Aula Editor emits its own nested font declarations. Keep post content
    // aligned with responsive typography without changing the editor globally.
    '& *': {
      fontFamily: `${theme.typography.fontFamily} !important`,
    },
    '& [data-testid="code-block"] *': {
      fontFamily: 'monospace !important',
    },
    '& .katex, & .katex *': {
      fontFamily: 'KaTeX_Main, serif !important',
    },
    '& img, & video': {
      display: 'block',
      width: '100%',
      maxWidth: '100%',
      height: 'auto',
      maxHeight: { xs: 360, sm: 480, md: 560, lg: 640 },
      objectFit: 'contain',
    },
    '& iframe': {
      maxWidth: '100%',
    },
    '& [data-testid="root-element"]': {
      width: '100% !important',
      maxWidth: '100%',
    },
    '& [data-slate-node="element"]': {
      width: '100% !important',
      maxWidth: '100%',
    },
    '& [data-testid="block-link-youtube"], & [data-testid="block-link-vimeo"], & [data-testid="block-link-nearpod"]':
      {
        width: { xs: '100% !important', md: '376px !important' },
        maxWidth: '100%',
      },
  },
  media: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    backgroundColor: theme.palette.action.hover,
    '& img, & video, & iframe': {
      display: 'block',
      width: '100%',
      maxWidth: '100%',
      height: 'auto',
      maxHeight: { xs: 360, sm: 480, md: 560, lg: 640 },
      objectFit: 'contain',
    },
  },
  edited: {
    px: 2,
    pt: 1,
    pb: 2,
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    fontStyle: 'italic',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    px: 2,
    py: 2,
  },
  divider: {
    width: 'calc(100% - 32px)',
    height: 0,
    minHeight: 0,
    mx: 2,
    my: 0,
    border: 0,
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  tagPlaceholder: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    minWidth: 0,
    gap: 1,
  },
  tagIcon: {
    flexShrink: 0,
    color: theme.palette.text.secondary,
    fontSize: '1.375rem',
    fontWeight: 600,
    lineHeight: 1,
  },
  tagPill: {
    px: 1.25,
    py: 0.5,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 99,
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    lineHeight: 1,
  },
  reactionPlaceholder: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    gap: 1,
    color: theme.palette.text.secondary,
  },
  reactionIcon: {
    flexShrink: 0,
    fontSize: '1.25rem',
    lineHeight: 1,
  },
  reactionLabel: {
    fontSize: '0.8125rem',
    lineHeight: 1.4,
  },
  commentsPlaceholder: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    gap: 1.25,
  },
  commentsAvatar: {
    flexShrink: 0,
    width: 32,
    height: 32,
    color: theme.palette.primary.contrastText,
  },
  commentsField: {
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    minWidth: 0,
    minHeight: 40,
    px: 1.5,
    borderRadius: 1,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.action.hover,
    fontSize: '0.8125rem',
  },
  menu: {},
  dialog: {
    width: { xs: 343, md: 560 },
    bgcolor: 'background.paper',
    borderRadius: '8px',
    boxSizing: 'border-box',
    p: '1.5rem',
    outline: 'none',
  },
  dialogHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '1rem',
    minHeight: { xs: '44px', sm: '40px' },
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 600,
    lineHeight: 1.6,
    letterSpacing: '0.15px',
    color: theme?.palette?.text?.primary,
    py: '0.25rem',
  },
  closeButton: {
    color: theme?.palette?.grey?.[500] ?? theme?.palette?.text?.secondary,
    flexShrink: 0,
    mr: '-0.25rem',
  },
  body: {
    fontFamily: theme?.typography?.fontFamily,
    fontWeight: theme?.typography?.fontWeightRegular ?? 400,
    fontStyle: 'normal',
    fontSize: '1rem',
    color: theme?.palette?.text?.primary,
    mb: '1.5rem',
    lineHeight: '180%',
    letterSpacing: '0.15px',
  },
  footer: {
    display: 'flex',
    gap: '0.75rem',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  buttonSx: {
    width: { xs: 'auto', sm: 'auto' },
    minWidth: '88px',
    height: '42px',
    minHeight: '42px',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: theme?.palette?.primary?.dark,
      borderColor: theme?.palette?.primary?.dark,
    },
    '&.MuiButton-outlinedPrimary:hover': {
      backgroundColor: 'transparent',
      borderColor: theme?.palette?.primary?.main,
    },
  },
  pinnedPostAlert: {
    m: 1,
  },
});
